import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DataTable } from "@/components/data-table/DataTable";
import type { ColumnDef } from "@/components/data-table/types";
import { useToast } from "@/hooks/use-toast";
import { BarChart3, FileText, Link as LinkIcon, Copy } from "lucide-react";
import { useLocation } from "wouter";

interface AdminReport {
  id: string;
  organizationName: string;
  sector: string;
  country: string | null;
  reportingYear: number;
  status: "draft" | "complete" | "finalized";
  completenessPercent: number;
  locked: boolean;
  createdAt: string;
  updatedAt: string;
  workspaceName: string | null;
  shareLink: {
    token: string;
    createdAt: string;
    expiresAt: string | null;
    viewCount: number;
    isRevoked: boolean;
  } | null;
  canViewReport: boolean;
}

interface ReportsResponse {
  data: AdminReport[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

async function fetchReports(page: number, search: string, statusFilter: string): Promise<ReportsResponse> {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: "10",
    search,
    status: statusFilter,
  });

  const response = await fetch(`/api/admin/reports?${params}`, { credentials: "include" });
  if (!response.ok) throw new Error("adminReports.errors.fetchFailed");
  return response.json();
}

export default function AdminReports() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [statusFilter, setStatusFilter] = useState<string>("all");

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const { data, isLoading } = useQuery({
    queryKey: ["/api/admin/reports", page, debouncedSearch, statusFilter],
    queryFn: () => fetchReports(page, debouncedSearch, statusFilter),
    enabled: user?.role === "admin",
  });

  if (user?.role !== "admin") {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="max-w-md bg-card rounded-3xl shadow-sm outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
          <div className="px-6 py-5">
            <h3 className="text-xl font-bold text-foreground">{"Access Denied"}</h3>
            <p className="text-sm text-slate-500 mt-1">{"You must be a platform administrator to view this page."}</p>
          </div>
        </div>
      </div>
    );
  }

  const handleCopyLink = async (token: string) => {
    const publicUrl = `${window.location.origin}/public/report/${token}`;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast({
        title: "Link copied",
        description: "Public report link copied to clipboard",
      });
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Could not copy link to clipboard",
        variant: "destructive",
      });
    }
  };

  const handleViewReport = (assessmentId: string) => {
    setLocation(`/admin/reports/${assessmentId}`);
  };

  const statusStyle = (status: string, locked: boolean) => {
    if (locked) return { bg: "bg-slate-900/10", text: "text-slate-900", dot: "bg-slate-900", label: "Locked" };
    switch (status) {
      case "finalized": return { bg: "bg-emerald-50", text: "text-emerald-600", dot: "bg-emerald-500", label: "Finalized" };
      case "complete": return { bg: "bg-blue-50", text: "text-blue-600", dot: "bg-blue-500", label: "Complete" };
      case "draft": return { bg: "bg-slate-100", text: "text-slate-600", dot: "bg-slate-400", label: "Draft" };
      default: return { bg: "bg-slate-100", text: "text-slate-600", dot: "bg-slate-400", label: status };
    }
  };

  const columns: ColumnDef<AdminReport>[] = [
    {
      id: "organization",
      header: "Organization",
      cell: (report) => <span className="font-semibold text-slate-900">{report.organizationName}</span>,
    },
    {
      id: "workspace",
      header: "Workspace",
      cell: (report) => (
        <span className="text-slate-700">{report.workspaceName || <span className="text-slate-400">—</span>}</span>
      ),
    },
    {
      id: "year",
      header: "Year",
      cell: (report) => <span className="text-slate-700">{report.reportingYear}</span>,
    },
    {
      id: "status",
      header: "Status",
      cell: (report) => {
        const ss = statusStyle(report.status, report.locked);
        return (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${ss.bg} ${ss.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${ss.dot}`} />
            {ss.label}
          </span>
        );
      },
    },
    {
      id: "completeness",
      header: "Completeness",
      cell: (report) => (
        <div className="flex items-center gap-2">
          <div className="w-16 bg-slate-100 rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all"
              style={{ width: `${report.completenessPercent}%` }}
            />
          </div>
          <span className="text-sm font-medium text-slate-700">
            {report.completenessPercent}%
          </span>
        </div>
      ),
    },
    {
      id: "shareLink",
      header: "Share Link",
      cell: (report) =>
        report.shareLink ? (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 rounded-full text-emerald-600 text-xs font-bold">
              <LinkIcon className="h-3 w-3" />
              {"Active"}
            </span>
            <span className="text-xs text-slate-400">
              {`${report.shareLink.viewCount} views`}
            </span>
          </div>
        ) : (
          <span className="text-slate-400 text-sm">—</span>
        ),
    },
    {
      id: "actions",
      header: "Actions",
      align: "right",
      cell: (report) => (
        <div className="flex items-center justify-end gap-1">
          {report.canViewReport && (
            <Button
              size="sm"
              variant="outline"
              className="rounded-xl"
              onClick={() => handleViewReport(report.id)}
            >
              <FileText className="h-4 w-4 mr-1 text-primary" />
              {"View Report"}
            </Button>
          )}
          {report.shareLink && (
            <Button
              size="sm"
              variant="ghost"
              className="rounded-xl"
              onClick={() => handleCopyLink(report.shareLink!.token)}
            >
              <Copy className="h-4 w-4 text-primary" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">
          {"Reports & Analytics"}
        </h1>
        <p className="text-slate-500 mt-2">
          {"View and manage generated reports across all companies"}
        </p>
      </div>

      <div className="bg-card rounded-3xl shadow-[0px_4px_6px_-4px_rgba(226,232,240,0.50),0px_10px_15px_-3px_rgba(226,232,240,0.50)] outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
        <div className="px-6 py-5 border-b border-border/30">
          <div className="flex items-center gap-3">
            <div className="w-[43px] h-[43px] bg-primary/10 rounded-2xl flex items-center justify-center">
              <BarChart3 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-foreground">
                {"All Reports"}
              </h3>
              <p className="text-sm text-slate-500">
                {`${data?.total || 0} total assessment(s) • Page ${page} of ${data?.totalPages || 1}`}
              </p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <DataTable
            columns={columns}
            data={data?.data ?? []}
            getRowId={(report) => report.id}
            isLoading={isLoading}
            emptyState={{ icon: FileText, title: "No reports found" }}
            search={{
              value: search,
              onChange: setSearch,
              placeholder: "Search by organization or workspace...",
            }}
            filters={
              <Select
                value={statusFilter}
                onValueChange={(value) => {
                  setStatusFilter(value);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-full sm:w-[180px] rounded-2xl bg-slate-50">
                  <SelectValue placeholder={"Filter by status"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{"All Statuses"}</SelectItem>
                  <SelectItem value="draft">{"Draft"}</SelectItem>
                  <SelectItem value="complete">{"Complete"}</SelectItem>
                  <SelectItem value="finalized">{"Finalized"}</SelectItem>
                </SelectContent>
              </Select>
            }
            pagination={
              data
                ? {
                    page,
                    pageSize: data.pageSize,
                    total: data.total,
                    totalPages: data.totalPages,
                    onPageChange: setPage,
                  }
                : undefined
            }
          />
        </div>
      </div>
    </div>
  );
}
